import { NavLink } from "react-router-dom";
import { useMemo } from "react";

import { useAuth } from "../context/AuthContext";

export default function Footer() {
  const { isLoggedIn } = useAuth();

  const usefulLinks = useMemo(
    () => [
      { label: "Home", to: "/" },
      { label: "Courses", to: "/courses" },
      { label: "Pricing", to: "/pricing" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
    []
  );

  const accountLinks = useMemo(
    () => [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
      ...(isLoggedIn ? [] : [{ label: "Login", to: "/login" }]),
    ],
    [isLoggedIn]
  );

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden">

      {/* ========== BACKGROUND DECORATION ========== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-3xl" />
      </div>

      {/* ========== TOP GRADIENT LINE ========== */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

      {/* ========== MAIN CONTENT ========== */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-5">

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 mb-10">

          {/* ========== BRAND SECTION ========== */}
          <div className="lg:col-span-2 space-y-4">
            <NavLink
              to="/"
              onClick={handleScrollTop}
              className="inline-flex items-center gap-4 group"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-white rounded-2xl p-3 shadow-2xl">
                  <img
                    src="images/logo.png"
                    alt="Well Spoken logo"
                    className="h-14 w-14 sm:h-16 sm:w-16 object-contain"
                  />
                </div>
              </div>

              <div>
                <p className="text-3xl sm:text-3xl font-black bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  Well Spoken
                </p>
                <p className="text-sm text-gray-400 font-semibold">
                  Put your best word forward!
                </p>
              </div>
            </NavLink>

            <p className="text-gray-400 leading-relaxed text-base sm:text-lg max-w-md">
              Practical English training for learners who want to speak with
              confidence in real-life situations.
            </p>

            <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
              <span className="text-2xl">✨</span>
              <span className="text-sm font-bold text-gray-300">
                Trusted by thousands of learners
              </span>
            </div>
          </div>

          {/* ========== LINKS (MOBILE FIX) ========== */}
          <div className="grid grid-cols-2 gap-8 sm:gap-12 lg:col-span-2">

            {/* Useful Links */}
            <nav>
              <h3 className="text-lg sm:text-xl font-black text-white mb-5 flex items-center gap-2">
                <span className="w-1.5 h-7 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                Useful Links
              </h3>
              <ul className="space-y-3">
                {usefulLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      onClick={handleScrollTop}
                      className="inline-block text-gray-400 hover:text-white font-semibold transition-all duration-300 hover:translate-x-1"
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Account Links */}
            <nav>
              <h3 className="text-lg sm:text-xl font-black text-white mb-5 flex items-center gap-2">
                <span className="w-1.5 h-7 bg-gradient-to-b from-indigo-500 to-pink-500 rounded-full" />
                Account
              </h3>
              <ul className="space-y-3">
                {accountLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      onClick={handleScrollTop}
                      className="inline-block text-gray-400 hover:text-white font-semibold transition-all duration-300 hover:translate-x-1"
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

          </div>
        </div>

        {/* ========== DIVIDER ========== */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-5" />

        {/* ========== BOTTOM BAR ========== */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <p className="text-gray-400">
            © {new Date().getFullYear()}{" "}
            <span className="font-black text-white">Well Spoken</span>. All rights reserved.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <p className="text-gray-400 flex items-center gap-2">
              <span className="text-xl">✨</span>
              <span className="font-semibold">Designed for professional learning</span>
            </p>

            <button
              onClick={handleScrollTop}
              className="w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 group"
              aria-label="Scroll to top"
            >
              <span className="text-xl sm:text-2xl group-hover:-translate-y-1 transition-transform duration-300">
                ↑
              </span>
            </button>
          </div>
        </div>
      </div>
    </footer>

  );
}

